import { APP_INITIALIZER, inject, InjectionToken, Provider } from '@angular/core';
import { IS_BROWSER } from '@libs/is-browser';
import { environment } from '@apps/clos-sessions/environments';
import type { MediaProvider } from './media-holder';

/** The query parameter that forces a provider outside production: `?forceProvider=twilio|livekit` */
export const FORCE_PROVIDER_PARAM = 'forceProvider';

const MEDIA_PROVIDERS: readonly MediaProvider[] = ['twilio', 'livekit'];

/** The provider a page URL's query forces; `null` in production, or when it forces none it knows */
export const readForcedProvider = (search: string, production: boolean): MediaProvider | null => {
  if (production) {
    return null;
  }

  const forced = new URLSearchParams(search).get(FORCE_PROVIDER_PARAM);
  return MEDIA_PROVIDERS.find((provider) => provider === forced) ?? null;
};

/**
 * A testing aid: the provider every connect attempt uses whatever the session says, or `null` to
 * let the session decide. Read once per application load, from the landing URL, which
 * `MEDIA_PROVIDER_OVERRIDE_CAPTURE` makes the app do at bootstrap; `null` on the server and in
 * production builds.
 */
export const MEDIA_PROVIDER_OVERRIDE = new InjectionToken<MediaProvider | null>(
  'MEDIA_PROVIDER_OVERRIDE',
  {
    providedIn: 'root',
    factory: () =>
      inject(IS_BROWSER)
        ? readForcedProvider(window.location.search, environment.production)
        : null,
  },
);

/**
 * Reads `MEDIA_PROVIDER_OVERRIDE` at bootstrap, while the page URL is still the one the tester
 * opened. A room cannot read it itself: sessions are not persisted, so a room URL first goes
 * through the join flow, and the join, room creation and invitation flows all navigate to the room
 * without the query, so by the time a room asks, `?forceProvider` is gone.
 */
export const MEDIA_PROVIDER_OVERRIDE_CAPTURE: Provider = {
  provide: APP_INITIALIZER,
  multi: true,
  deps: [MEDIA_PROVIDER_OVERRIDE],
  useFactory: () => () => undefined,
};
